/**
 * Gap-aware “track coverage” intervals for map-style rows (Datetime + fallbacks).
 * Aligns with map line breaks where consecutive samples differ by more than MAP_TRACK_GAP_THRESHOLD_MS.
 */

export const MAP_TRACK_GAP_THRESHOLD_MS = 10_000;

export type MapTrackCoverageSegment = { startMs: number; endMs: number };

/** Milliseconds from a map/timeseries row (same field order as LiveMapTimeSeries getTimestamp). */
export function mapRowTimestampMs(d: unknown): number | null {
  if (d == null || typeof d !== 'object') {return null;}
  const o = d as Record<string, unknown>;
  const timestamp = o.Datetime ?? o.timestamp ?? o.time ?? o.datetime;
  if (timestamp == null) {return null;}
  if (timestamp instanceof Date) {
    const t = timestamp.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof timestamp === 'number') {
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Sorted unique finite timestamps from map rows. */
export function extractSortedTimestampsMs(rows: unknown[]): number[] {
  if (!Array.isArray(rows) || rows.length === 0) {return [];}
  const set = new Set<number>();
  for (const row of rows) {
    const ms = mapRowTimestampMs(row);
    if (ms != null) {set.add(ms);}
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Merge consecutive samples into [startMs, endMs] segments when gaps between sorted points are <= gapMs.
 */
export function buildCoverageSegmentsMs(sortedMs: number[], gapMs: number): MapTrackCoverageSegment[] {
  if (sortedMs.length === 0) {return [];}
  const segments: MapTrackCoverageSegment[] = [];
  let segStart = sortedMs[0];
  let segEnd = sortedMs[0];
  for (let i = 1; i < sortedMs.length; i++) {
    const t = sortedMs[i];
    if (t - segEnd <= gapMs) {
      segEnd = t;
    } else {
      segments.push({ startMs: segStart, endMs: segEnd });
      segStart = t;
      segEnd = t;
    }
  }
  segments.push({ startMs: segStart, endMs: segEnd });
  return segments;
}

export function isMsInsideSegments(ms: number, segments: readonly MapTrackCoverageSegment[]): boolean {
  if (!Number.isFinite(ms) || segments.length === 0) {return false;}
  for (const s of segments) {
    if (ms >= s.startMs && ms <= s.endMs) {return true;}
  }
  return false;
}
